class CustomFacetsForm extends HTMLElement {
    constructor() {
        super();
        this.filterGroups = this.querySelectorAll("[data-filter-group]");
        this.filterClearButtons = this.querySelectorAll("[data-filter-clear]");
        this.buttonEvents = ["click", "touchstart", "keydown"];
        this.modal = this.closest("modal-element");
        this.collectionCountSelector = ".collection-banner__count";
        this.accordionContentSelector = ".accordion__content";
        this.accordionWrapperSelector = ".accordion__wrapper";
        this.sectionId = this.dataset.sectionId;
        this.updatingHtml = false;
        this.shopifySection = document.getElementById(
            `shopify-section-${this.sectionId}`
        );
        this.form = this.querySelector("form");
        // this object is used to optimize caching of modified states and fetched html, to avoid redundant operations and preserve initial states
        this.cachedStates = {
            modifiedInputs: new Map(),
            modifiedActiveFilters: new Map(),
            modifiedFiltersCount: new Map(),
            cachedFetchHtml: new Map(),
        };
        this.submitButton = this.querySelector('button[type="submit"]');
        this.resetButton = this.querySelector('button[type="reset"]');
    }

    connectedCallback() {
        this.cacheInitialFetchHtml();
        this.setupEventListeners();
    }

    cacheInitialFetchHtml() {
        const initialHtml = document.querySelector(
            `#shopify-section-${this.sectionId}`
        );
        this.cachedStates.cachedFetchHtml.set(
            window.location.href,
            initialHtml.innerHTML
        );
    }

    setupEventListeners() {
        this.modal.addEventListener(
            "modal:close",
            this.resetActiveFilters.bind(this)
        );
        this.form.addEventListener("change", (e) => this.onFormChanged(e));
        this.filterClearButtons.forEach((filterButton) => {
            this.buttonEvents.forEach((event) => {
                filterButton.addEventListener(event, (e) =>
                    this.handleFilterClear(e, filterButton, event)
                );
            });
        });
        this.form.addEventListener("submit", (e) => this.onFormComplete(e));
        this.form.addEventListener("reset", (e) =>
            this.onFormComplete(e, true)
        );
        if (!this.hasAttribute("data-pagination-listeners-bound")) {
            this.setAttribute("data-pagination-listeners-bound", "true");
            document.addEventListener("custom-facet-link:clicked", (e) =>
                this.preFetchAndUpdate(e.detail.url)
            );
        }
    }

    preFetchAndUpdate(url) {
        document.documentElement.dispatchEvent(
            new CustomEvent("theme:loading:start", { bubbles: true })
        );
        try {
            if (this.cachedStates.cachedFetchHtml.has(url)) {
                this.updateHtml(this.cachedStates.cachedFetchHtml.get(url));
            } else {
                fetch(url)
                    .then((response) => response.text())
                    .then((responseText) => {
                        const html = responseText;
                        this.cachedStates.cachedFetchHtml.set(url, html);
                        this.updateHtml(html);
                    });
            }
            // const baseUrl = window.location.href
            const clonedUrl = new URL(url, window.location.href);
            clonedUrl.searchParams.delete("section_id");
            clonedUrl.hash = "";
            history.replaceState({}, "", clonedUrl.toString());
        } catch (error) {
            console.warn("Error fetching filter data:", error);
        } finally {
            document.documentElement.dispatchEvent(
                new CustomEvent("theme:loading:end", { bubbles: true })
            );
        }
    }

    onFormComplete(e, isReset) {
        e.preventDefault();
        if (!isReset && !this.cachedStates.modifiedInputs.size) return;
        const url = this.urlBuilder(isReset);
        this.preFetchAndUpdate(url);
    }

    handleSearchTitle(html) {
        const searchTitle = this.shopifySection.querySelector("#search-title");
        const newSearchTitle = html.querySelector("#search-title");
        searchTitle &&
            newSearchTitle &&
            (searchTitle.innerHTML = newSearchTitle.innerHTML);
    }

    handleCollectionCounts(html) {
        const collectionCount = this.shopifySection.querySelector(
            this.collectionCountSelector
        );
        const newCollectionCount = html.querySelector(
            this.collectionCountSelector
        );
        collectionCount &&
            newCollectionCount &&
            (collectionCount.innerText = newCollectionCount.innerText);
    }

    handleCollectionList(html) {
        const innerSection =
            this.shopifySection.querySelector("[data-collection]");
        const newInnerSection = html.querySelector(
            ".shopify-section [data-collection]"
        );
        if (innerSection && newInnerSection) {
            const currentProductListAttributes =
                innerSection.querySelector(".product-list")?.attributes;
            const newProductList =
                newInnerSection.querySelector(".product-list");
            if (currentProductListAttributes && newProductList) {
                for (let attr of currentProductListAttributes) {
                    newProductList.setAttribute(attr.name, attr.value);
                }
            }
            innerSection.replaceChildren(
                ...document.importNode(newInnerSection, true).childNodes
            );
            // Swym only binds wishlist buttons present at page load
            window._swat?.initializeActionButtons?.(
                `#shopify-section-${this.sectionId}`
            );
        }
    }

    handleSelectedFiltersButtons(html) {
        const filterButtons = this.shopifySection.querySelectorAll(
            "[data-filter-button]"
        );
        const newfilterButtonHtml = html.querySelector("[data-filter-button]");
        if (newfilterButtonHtml) {
            filterButtons.forEach((button) => {
                button.innerHTML = newfilterButtonHtml.innerHTML;
            });
        }
        const filterModal = document.querySelector(
            "modal-element#facets-drawer"
        );
        if (filterModal) {
            if (window["close_facets-drawer"]) {
                window["close_facets-drawer"]();
            }
        }
    }

    handleFilters(html) {
        const filterGroups = this.querySelectorAll("[data-filter-group]");
        const newFacetForm = html.querySelector(
            "#facets-drawer .modal__body custom-facets-form"
        );
        const filterCount = this.query;
        const newResetButton = newFacetForm.querySelector(
            'button[type="reset"]'
        );
        const newFilterGroups = newFacetForm.querySelectorAll(
            "[data-filter-group]"
        );

        this.resetButton.disabled = newResetButton.disabled;
        filterGroups.forEach((group) => {
            const groupHandle = group.dataset.filterGroup;
            const newGroup = Array.from(newFilterGroups).find(
                (newGroup) => newGroup.dataset.filterGroup === groupHandle
            );
            if (newGroup) {
                const newGroupContent = newGroup.querySelector(
                    this.accordionContentSelector
                );
                const newGroupActiveList =
                    newGroup.querySelector(".active-filters");
                const currentGroupContent = group.querySelector(
                    this.accordionContentSelector
                );
                const currentGroupActiveList =
                    group.querySelector(".active-filters");
                const currentGroupCount = group.querySelector(
                    "[data-current-filter-count]"
                );
                const newGroupCount = newFacetForm.querySelector(
                    `[data-filter-group="${groupHandle}"] [data-current-filter-count]`
                );
                if (currentGroupCount && newGroupCount) {
                    currentGroupCount.textContent = newGroupCount.textContent;
                }
                if (currentGroupContent && newGroupContent) {
                    currentGroupContent.replaceChildren(
                        ...document.importNode(newGroupContent, true).childNodes
                    );
                }
                if (currentGroupActiveList && newGroupActiveList) {
                    currentGroupActiveList.replaceChildren(
                        ...document.importNode(newGroupActiveList, true)
                            .childNodes
                    );
                }
            }
        });
    }

    scrollToProductList() {
        document.querySelector(".product-list")?.scrollIntoView({
            block: "start",
            behavior: "smooth",
        });
    }

    updateHtml(html) {
        const parsedHtml = new DOMParser().parseFromString(html, "text/html");
        const focusedElement = document.activeElement;
        this.updatingHtml = true;
        this.handleCollectionCounts(parsedHtml);
        this.handleSearchTitle(parsedHtml);
        this.handleCollectionList(parsedHtml);
        this.handleSelectedFiltersButtons(parsedHtml);
        this.handleFilters(parsedHtml);
        focusedElement?.id &&
            document.getElementById(focusedElement.id) &&
            document.getElementById(focusedElement.id).focus();
        if ("requestIdleCallback" in window) {
            requestIdleCallback(this.scrollToProductList, { timeout: 500 });
        } else {
            requestAnimationFrame(this.scrollToProductList);
        }
        this.updatingHtml = false;
    }

    urlBuilder(isReset) {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set("section_id", this.sectionId);
        const sParam = new URLSearchParams(window.location.search).get("q");
        if (sParam) {
            url.searchParams.set("q", sParam);
        }
        // handle search params
        if (!isReset) {
            const fData = new FormData(this.form);
            fData.forEach((value, key) => url.searchParams.append(key, value));
        }
        return url.toString();
    }

    handleCheckboxFilterChange(
        isChecked,
        activeFiltersList,
        label,
        filterCount
    ) {
        const currentCount = parseInt(
            filterCount?.textContent.match(/\d+/)?.[0] || 0
        );

        if (activeFiltersList) {
            let activeFilterInner =
                activeFiltersList.querySelector("[data-active-list]");

            if (!activeFilterInner) {
                activeFilterInner = document.createElement("span");
                activeFilterInner.setAttribute("data-active-list", "");
                activeFilterInner.classList.add("active-filters__list");
                activeFiltersList.appendChild(activeFilterInner);
            }

            const currentText = activeFilterInner.innerText;
            activeFilterInner.innerText = isChecked
                ? currentText
                    ? `${currentText}, ${label}`
                    : label
                : currentText
                      .replace(new RegExp(`,?\\s*${label}`), "")
                      .replace(/^,\s*/, "");
        }

        // Update filter count
        if (filterCount) {
            const newCount = isChecked
                ? currentCount + 1
                : Math.max(0, currentCount - 1);
            filterCount.textContent = newCount > 0 ? `(${newCount})` : "";
        }
    }

    handleRadioFilterChange(target, activeFiltersList, label) {
        if (!activeFiltersList) return;

        let activeFilterInner =
            activeFiltersList.querySelector("[data-active-list]");

        if (!activeFilterInner) {
            activeFilterInner = document.createElement("span");
            activeFilterInner.setAttribute("data-active-list", "");
            activeFilterInner.classList.add("active-filters__list");
            activeFiltersList.appendChild(activeFilterInner);
        }

        activeFilterInner.innerText = label;
    }

    updateInitialStates(input, activeFiltersList, filterCount) {
        const { modifiedInputs, modifiedActiveFilters, modifiedFiltersCount } =
            this.cachedStates;

        // Cache initial states only once
        if (!modifiedActiveFilters.has(activeFiltersList)) {
            modifiedActiveFilters.set(
                activeFiltersList,
                activeFiltersList.innerHTML
            );
        }

        if (filterCount && !modifiedFiltersCount.has(filterCount)) {
            modifiedFiltersCount.set(filterCount, filterCount.textContent);
        }

        // Toggle input tracking
        if (modifiedInputs.has(input)) {
            modifiedInputs.delete(input);
        } else {
            modifiedInputs.set(input, !input.checked);
        }

        this.submitButton.disabled = modifiedInputs.size === 0;
    }

    onFormChanged(e) {
        const target = e.target;
        const accordionWrapper = e.target.closest(
            this.accordionWrapperSelector
        );
        if (!accordionWrapper) return;
        const activeFiltersList =
            accordionWrapper.querySelector(".active-filters");
        const label =
            target.dataset.label || target.nextElementSibling?.innerText || "";
        const filterCount = accordionWrapper.querySelector(
            "[data-current-filter-count]"
        );
        this.updateInitialStates(target, activeFiltersList, filterCount);
        if (target.type === "checkbox") {
            this.handleCheckboxFilterChange(
                target.checked,
                activeFiltersList,
                label,
                filterCount
            );
        } else if (target.type === "radio") {
            this.handleRadioFilterChange(target, activeFiltersList, label);
        }
    }

    resetActiveFilters() {
        // Restore filters to initial state using Proxy
        if (!this.updatingHtml) {
            this.cachedStates.modifiedInputs.forEach((prop, filter) => {
                filter.checked = prop;
                prop
                    ? filter.setAttribute("checked", "checked")
                    : filter.removeAttribute("checked");
            });
            this.cachedStates.modifiedActiveFilters.forEach(
                (prop, activeFilter) => {
                    activeFilter.innerHTML = prop;
                }
            );
            this.cachedStates.modifiedFiltersCount.forEach(
                (prop, filterCount) => {
                    filterCount.textContent = prop;
                }
            );
        }
        this.querySelectorAll("accordion-select").forEach((accordion) => {
            accordion.close();
        });
        this.submitButton.disabled = true;
        this.cachedStates.modifiedInputs.clear();
        this.cachedStates.modifiedActiveFilters.clear();
        this.cachedStates.modifiedFiltersCount.clear();
    }

    handleFilterClear(e, filterButton, event) {
        if (event === "keydown" && e.key !== "Enter" && e.key !== " ") {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const currentAccordion = filterButton.closest(
            this.accordionWrapperSelector
        );
        if (!currentAccordion) return;
        const { modifiedInputs, modifiedActiveFilters, modifiedFiltersCount } =
            this.cachedStates;
        // Process checked filters
        const filters = currentAccordion.querySelectorAll('[type="checkbox"]');
        filters.forEach((filter) => {
            if (!filter.checked) return;

            filter.checked = false;
            filter.removeAttribute("checked");

            if (modifiedInputs.has(filter)) {
                modifiedInputs.delete(filter);
            } else {
                modifiedInputs.set(filter, true);
            }
        });

        // Cache and clear active filters container, no need to delete value inside map
        const activeFiltersContainer =
            currentAccordion.querySelector(".active-filters");
        if (activeFiltersContainer) {
            if (!modifiedActiveFilters.has(activeFiltersContainer)) {
                modifiedActiveFilters.set(
                    activeFiltersContainer,
                    activeFiltersContainer.innerHTML
                );
            }
            activeFiltersContainer.innerHTML = "";
        }

        // Cache and clear filter count, no need to delete value inside map
        const filterCount = currentAccordion.querySelector(
            "[data-current-filter-count]"
        );
        if (filterCount) {
            if (!modifiedFiltersCount.has(filterCount)) {
                modifiedFiltersCount.set(filterCount, filterCount.textContent);
            }
            filterCount.textContent = "";
        }

        this.submitButton.disabled = modifiedInputs.size === 0;
    }
}

if (!customElements.get("custom-facets-form")) {
    customElements.define("custom-facets-form", CustomFacetsForm);
}

class CustomFacetLink extends HTMLElement {
    constructor() {
        super();
        this.link = this.querySelector("a");
    }

    connectedCallback() {
        this.link?.addEventListener("click", (e) => this.onLinkClick(e));
    }

    onLinkClick(e) {
        e.preventDefault();
        const url = this.link.href;
        // dispatch a custom event to notify other components
        this.dispatchEvent(
            new CustomEvent("custom-facet-link:clicked", {
                detail: { url: url },
                bubbles: true,
            })
        );
    }
}

if (!customElements.get("custom-facet-link")) {
    customElements.define("custom-facet-link", CustomFacetLink);
}
